import uuid from 'uuid/v4';
import moment from 'moment';
import { last, isEmpty } from 'lodash';

import actionTypes from 'constants/chatActionTypes';
import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';

import * as api from './api';
import * as types from './actionTypes';
import { getChat, getThread } from './selectors';

/**
 * Start chat
 *`
 * @returns {Function}
 */
export const startChat = () => async dispatch => {
  try {
    // eslint-disable-next-line @typescript-eslint/camelcase
    const dialog_id = uuid();

    await dispatch({
      type: types.START_CHAT_REQUEST,
      payload: {
        id: dialog_id,
        thread: [],
      },
    });

    await api.startChatRequest({
      dialog_id,
    });

    const { data } = await api.sendMessageRequest({
      dialog_id,
      utterance: '',
    });

    await dispatch({
      type: types.START_CHAT_SUCCESS,
    });

    await dispatch({
      type: types.SAVE_BOT_RESPONSE,
      payload: {
        message: {
          ...data,
          owner: 'bot',
          type: 'message',
          timestamp: moment().format(),
        },
      },
    });
  } catch (error) {
    await dispatch({
      type: types.START_CHAT_ERROR,
      payload: {
        error: error?.response?.data?.detail ?? 'Oops, sth went wrong..',
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Restart Chat
 *
 * @returns {Function}
 */
export const restartChat = () => async (dispatch, getState) => {
  try {
    // eslint-disable-next-line @typescript-eslint/camelcase
    const dialog_id = uuid();

    const thread = [
      ...getThread(getState()),
      {
        type: 'divider',
      },
    ];

    await dispatch({
      type: types.START_CHAT_REQUEST,
      payload: {
        id: dialog_id,
        thread,
      },
    });

    const { data } = await api.sendMessageRequest({
      dialog_id,
      utterance: '',
    });

    await dispatch({
      type: types.START_CHAT_SUCCESS,
    });

    await dispatch({
      type: types.SAVE_BOT_RESPONSE,
      payload: {
        message: {
          ...data,
          owner: 'bot',
          type: 'message',
          timestamp: moment().format(),
        },
      },
    });
  } catch (error) {
    await dispatch({
      type: types.START_CHAT_ERROR,
      payload: {
        error: error?.response?.data?.detail ?? 'Oops, sth went wrong..',
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Send a message
 *
 * @param utterance
 */
export const sendMessage = (utterance, dataModel = {}) => async (
  dispatch,
  getState,
) => {
  try {
    const lastBotResponse = last(getThread(getState()));
    const lastBotResponseAction = (
      lastBotResponse?.action_type ?? ''
    ).toLowerCase();

    if (actionTypes.includes(lastBotResponseAction)) {
      await dispatch(restartChat());
    } else {
      if (lastBotResponseAction !== 'announcement') {
        await dispatch({
          type: types.SAVE_USER_RESPONSE,
          payload: {
            message: {
              owner: 'user',
              type: 'message',
              utterance: !isEmpty(dataModel.context)
                ? dataModel.label
                : utterance,
              timestamp: moment().format(),
            },
          },
        });
      }

      // eslint-disable-next-line @typescript-eslint/camelcase
      const dialog_id = getChat(getState()).id;

      await dispatch({
        type: types.SEND_MESSAGE_REQUEST,
      });

      const { data } = await api.sendMessageRequest({
        dialog_id,
        utterance,
        context: dataModel.context,
      });

      await dispatch({
        type: types.SEND_MESSAGE_SUCCESS,
      });

      await dispatch({
        type: types.SAVE_BOT_RESPONSE,
        payload: {
          message: {
            ...data,
            owner: 'bot',
            type: 'message',
            timestamp: moment().format(),
          },
        },
      });

      if (data.action_type.toLowerCase() === 'announcement') {
        await dispatch(sendMessage('[noinput]'));
      }
    }
  } catch (error) {
    await dispatch({
      type: types.SEND_MESSAGE_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Stop chat
 *
 * @returns {{type: string}}
 */
export const stopChat = () => ({
  type: types.STOP_CHAT_REQUEST,
});

/**
 * Toggle minimize chat
 *
 * @param isOpen
 * @returns {{type: string, payload: {isOpen: *}}}
 */
export const toggleMinimizeChat = isOpen => ({
  type: types.TOGGLE_MINIMIZE_CHAT_REQUEST,
  payload: {
    isOpen,
  },
});
